<template>
	<div class="mt-2">
		<template v-if="loading">
			<skeletonBuilder 
				:showChart="false"
				:cols="1"
				:rows="10"
			/>
		</template>

		<template v-else>
			<!-- На заполнение -->
			<div v-if="agentCompany.check_status === 'fill'" class="agent-dashboard">
				<!-- HERO -->
				<b-card class="dashboard-hero-card mb-4" body-class="p-4 p-md-5">
					<div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
						<div class="mb-3 mb-md-0">
							<h2 class="mb-2 text-white">Добро пожаловать! 👋</h2>
							<p class="lead text-white-80 mb-0">Завершите заполнение агентской карточки для начала сотрудничества</p>
						</div>
						<div class="text-nowrap hero-actions text-right">
							<b-button
								variant="success"
								size="lg"
								@click="isSidebarOpen = true"
								style="text-transform: uppercase; height: 50px;"
							>
								<i class="fas fa-user-edit mr-2"></i>
								Заполнить карточку
							</b-button>
						</div>
					</div>
				</b-card>

				<!-- Основной контент -->
				<b-row>
					<b-col cols="12" md="8">
						<b-card class="mb-4">
							<h4 class="card-title mb-3">📝 Что нужно заполнить:</h4>
							<ul class="list-unstyled">
								<li class="mb-2">
									<i class="fas fa-user text-info mr-2"></i>
									<strong>Личные данные:</strong> ФИО, контактная информация, адрес
								</li>
								<li class="mb-2">
									<i class="fas fa-id-card text-info mr-2"></i>
									<strong>Документы:</strong> Сканы паспорта, ИНН, банковские реквизиты
								</li>
								<li class="mb-2">
									<i class="fas fa-briefcase text-info mr-2"></i>
									<strong>Бизнес-информация:</strong> Тип деятельности, регион работы, опыт
								</li>
								<li class="mb-2">
									<i class="fas fa-file-contract text-info mr-2"></i>
									<strong>Дополнительные документы:</strong> Справки, лицензии (при необходимости)
								</li>
							</ul>
						</b-card>

						<b-card class="mb-4">
							<h4 class="card-title mb-3">⚠️ Важные требования:</h4>
							<div class="alert alert-warning">
								<i class="fas fa-exclamation-triangle mr-2"></i>
								<strong>Внимание:</strong> Заполните все поля и прикрепите сканы или фотографии необходимых документов.
							</div>
							<ul class="list-unstyled">
								<li class="mb-2">
									<i class="fas fa-check-circle text-success mr-2"></i>
									Все документы должны быть четкими и читаемыми
								</li>
								<li class="mb-2">
									<i class="fas fa-check-circle text-success mr-2"></i>
									Контактная информация должна быть актуальной
								</li>
								<li class="mb-2">
									<i class="fas fa-check-circle text-success mr-2"></i>
									Банковские реквизиты должны быть корректными
								</li>
							</ul>
						</b-card>

						<b-card class="mb-4">
							<h4 class="card-title mb-3">⏳ После заполнения:</h4>
							<p class="mb-3">
								После отправки карточки на проверку наши юристы изучат все документы, и по результатам отправят вам уведомление.
							</p>
							<div class="text-center mt-4 mb-3">
								<b-button 
									variant="success" 
									size="lg"
									@click="isSidebarOpen = true"
									style="text-transform: uppercase; height: 50px;"
								>
									<i class="fas fa-user-edit mr-2"></i>
									Начать заполнение
								</b-button>
							</div>
						</b-card>
					</b-col>

					<b-col cols="12" md="4">
						<b-card class="mb-4">
							<h5 class="card-title mb-3">💼 Ваши комиссии:</h5>
							<div class="commission-item mb-2">
								<div class="h5 mb-0 text-success font-weight-bold">{{ agentCompany.first_order_commission_rate || 30 }}%</div>
								<small class="text-muted">От маржинальной прибыли<br />с первой поставки</small>
							</div>
							<div class="commission-item mb-2">
								<div class="h5 mb-0 text-success font-weight-bold">{{ agentCompany.first_year_order_commission_rate || 25 }}%</div>
								<small class="text-muted">От маржинальной прибыли<br />в течение первого года</small>
							</div>
							<div class="commission-item mb-2">
								<div class="h5 mb-0 text-success font-weight-bold">{{ agentCompany.next_orders_commission_rate || 20 }}%</div>
								<small class="text-muted">От маржинальной прибыли до конца сотрудничества после первого года</small>
							</div>
						</b-card>

						<b-card>
							<h5 class="card-title mb-3">📞 Нужна помощь?</h5>
							<p class="mb-2">Если у вас есть вопросы по заполнению карточки или нужна помощь, свяжитесь с нами в Telegram:</p>
							<div class="text-center mt-2">
								<a 
									href="https://t.me/TANAIS_OPERATION" 
									target="_blank" 
									class="btn btn-primary"
								>
									<i class="fab fa-telegram mr-2"></i>
									Написать в Telegram
								</a>
							</div>
						</b-card>
					</b-col>
				</b-row>
			</div>

			<!-- Проверка -->
			<div v-else-if="agentCompany.check_status === 'pending'" class="agent-dashboard">
				<!-- HERO -->
				<b-card class="dashboard-hero-card mb-4" body-class="p-4 p-md-5">
					<div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
						<div class="mb-3 mb-md-0">
							<h2 class="mb-2 text-white">Данные на проверке 🔎</h2>
							<p class="lead text-white-80 mb-0">Мы получили ваши данные и сейчас проверяем документы</p>
						</div>
						<div class="text-nowrap hero-actions text-right">
							<b-button
								variant="primary"
								size="lg"
								@click="fetchUserCompany"
								style="text-transform: uppercase; height: 50px;"
							>
								<i class="fas fa-sync-alt mr-2"></i>
								Обновить статус
							</b-button>
						</div>
					</div>
				</b-card>

				<!-- Основной контент -->
				<b-row>
					<b-col cols="12" md="8">
						<b-card class="mb-4">
							<h4 class="card-title mb-3">🔍 Что мы проверяем:</h4>
							<ul class="list-unstyled">
								<li class="mb-2">
									<i class="fas fa-clock text-primary mr-2"></i>
									<strong>Контактная информация:</strong> Адрес, телефон, email
								</li>
								<li class="mb-2">
									<i class="fas fa-clock text-primary mr-2"></i>
									<strong>Документы:</strong> ИНН, паспорт, банковские реквизиты и другие
								</li>
								<li class="mb-2">
									<i class="fas fa-clock text-primary mr-2"></i>
									<strong>Бизнес-данные:</strong> Тип деятельности, регион работы
								</li>
								<li class="mb-2">
									<i class="fas fa-clock text-primary mr-2"></i>
									<strong>Финансовые условия:</strong> Комиссионные ставки и условия сотрудничества
								</li>
							</ul>
						</b-card>

						<b-card class="mb-4">
							<h4 class="card-title mb-3">⏳ Время проверки:</h4>
							<p class="mb-3">
								Проверка документов обычно занимает несколько рабочих дней. 
								Мы тщательно изучаем все предоставленные материалы, чтобы обеспечить качественное сотрудничество.
							</p>
							<div class="alert alert-info">
								<i class="fas fa-info-circle mr-2"></i>
								<strong>Уведомления:</strong> Как только проверка будет завершена, вы получите уведомление в личный кабинет.
							</div>
						</b-card>

						<b-card class="mb-4">
							<h4 class="card-title mb-3">📋 Что дальше:</h4>
							<p class="mb-3">
								После успешной проверки вам будет предложено подписать договор оферты, 
								и вы сможете начать работу в системе.
							</p>
							<div class="text-center mt-4 mb-3">
								<b-button 
									variant="primary" 
									size="lg"
									@click="fetchUserCompany"
									style="text-transform: uppercase; height: 50px;"
								>
									<i class="fas fa-sync-alt mr-2"></i>
									Проверить статус
								</b-button>
							</div>
						</b-card>
					</b-col>

					<b-col cols="12" md="4">
						<b-card class="mb-4">
							<h5 class="card-title mb-3">💼 Ваши комиссии:</h5>
							<div class="commission-item mb-2">
								<div class="h5 mb-0 text-success font-weight-bold">{{ agentCompany.first_order_commission_rate || 30 }}%</div>
								<small class="text-muted">От маржинальной прибыли<br />с первой поставки</small>
							</div>
							<div class="commission-item mb-2">
								<div class="h5 mb-0 text-success font-weight-bold">{{ agentCompany.first_year_order_commission_rate || 25 }}%</div>
								<small class="text-muted">От маржинальной прибыли<br />в течение первого года</small>
							</div>
							<div class="commission-item mb-2">
								<div class="h5 mb-0 text-success font-weight-bold">{{ agentCompany.next_orders_commission_rate || 20 }}%</div>
								<small class="text-muted">От маржинальной прибыли до конца сотрудничества после первого года</small>
							</div>
						</b-card>

						<b-card>
							<h5 class="card-title mb-3">📞 Нужна помощь?</h5>
							<p class="mb-2">Если у вас есть вопросы по процессу проверки или нужна помощь, свяжитесь с нами в Telegram:</p>
							<div class="text-center mt-2">
								<a 
									href="https://t.me/TANAIS_OPERATION" 
									target="_blank" 
									class="btn btn-primary"
								>
									<i class="fab fa-telegram mr-2"></i>
									Написать в Telegram
								</a>
							</div>
						</b-card>
					</b-col>
				</b-row>
			</div>

			<!-- На доработку -->
			<div v-else-if="agentCompany.check_status === 'revision'" class="agent-dashboard">
				<!-- HERO -->
				<b-card class="dashboard-hero-card mb-4" body-class="p-4 p-md-5">
					<div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
						<div class="mb-3 mb-md-0">
							<h2 class="mb-2 text-white">Требуется доработка ✍️</h2>
							<p class="lead text-white-80 mb-0">Нашим юристам понадобились некоторые уточнения</p>
						</div>
						<div class="text-nowrap hero-actions text-right">
							<b-button
								variant="warning"
								size="lg"
								@click="isSidebarOpen = true"
								style="text-transform: uppercase; height: 50px;"
							>
								<i class="fas fa-edit mr-2"></i>
								Внести изменения
							</b-button>
						</div>
					</div>
				</b-card>

				<!-- Основной контент -->
				<b-row>
					<b-col cols="12" md="8">
						<b-card class="mb-4">
							<h4 class="card-title mb-3">📝 Комментарий от юристов:</h4>
							<div 
								class="revision-comment p-3 bg-light rounded"
								style="border-left: 4px solid #ffc107;"
								v-html="revisionComment"
							></div>
						</b-card>

						<b-card class="mb-4">
							<h4 class="card-title mb-3">🔧 Что нужно исправить:</h4>
							<ul class="list-unstyled">
								<li class="mb-2">
									<i class="fas fa-exclamation-triangle text-warning mr-2"></i>
									<strong>Проверьте документы:</strong> Убедитесь, что все сканы четкие и читаемые
								</li>
								<li class="mb-2">
									<i class="fas fa-exclamation-triangle text-warning mr-2"></i>
									<strong>Контактная информация:</strong> Проверьте корректность адреса и телефона
								</li>
								<li class="mb-2">
									<i class="fas fa-exclamation-triangle text-warning mr-2"></i>
									<strong>Банковские реквизиты:</strong> Убедитесь в правильности ИНН и расчетного счета
								</li>
								<li class="mb-2">
									<i class="fas fa-exclamation-triangle text-warning mr-2"></i>
									<strong>Дополнительные данные:</strong> Заполните все обязательные поля
								</li>
							</ul>
						</b-card>

						<b-card class="mb-4">
							<h4 class="card-title mb-3">✅ После исправления:</h4>
							<p class="mb-3">
								После внесения всех необходимых изменений ваша карточка будет повторно отправлена на проверку. 
								Обычно повторная проверка занимает 1-2 рабочих дня.
							</p>
							<div class="text-center mt-4 mb-3">
								<b-button 
									variant="warning" 
									size="lg"
									@click="isSidebarOpen = true"
									style="text-transform: uppercase; height: 50px;"
								>
									<i class="fas fa-edit mr-2"></i>
									Исправить и отправить повторно
								</b-button>
							</div>
						</b-card>
					</b-col>

					<b-col cols="12" md="4">
						<b-card class="mb-4">
							<h5 class="card-title mb-3">💼 Ваши комиссии:</h5>
							<div class="commission-item mb-2">
								<div class="h5 mb-0 text-success font-weight-bold">{{ agentCompany.first_order_commission_rate || 30 }}%</div>
								<small class="text-muted">От маржинальной прибыли<br />с первой поставки</small>
							</div>
							<div class="commission-item mb-2">
								<div class="h5 mb-0 text-success font-weight-bold">{{ agentCompany.first_year_order_commission_rate || 25 }}%</div>
								<small class="text-muted">От маржинальной прибыли<br />в течение первого года</small>
							</div>
							<div class="commission-item mb-2">
								<div class="h5 mb-0 text-success font-weight-bold">{{ agentCompany.next_orders_commission_rate || 20 }}%</div>
								<small class="text-muted">От маржинальной прибыли до конца сотрудничества после первого года</small>
							</div>
						</b-card>

						<b-card>
							<h5 class="card-title mb-3">📞 Нужна помощь?</h5>
							<p class="mb-2">Если у вас есть вопросы по доработке или нужна помощь с заполнением, свяжитесь с нами в Telegram:</p>
							<div class="text-center mt-2">
								<a 
									href="https://t.me/TANAIS_OPERATION" 
									target="_blank" 
									class="btn btn-primary"
								>
									<i class="fab fa-telegram mr-2"></i>
									Написать в Telegram
								</a>
							</div>
						</b-card>
					</b-col>
				</b-row>
			</div>

			<!-- Отказ -->
			<b-jumbotron style="max-width: 900px;" v-else-if="agentCompany.check_status === 'rejected'">
				<template #header>
					<h3 class="display-6">К сожалению, отказ 😔</h3>
				</template>

				<template #lead>
					<div style="margin-top: 35px; max-width: 750px;">
						Нам жаль, но на данном этапе мы не можем продолжить сотрудничество. <br><br>
						Причина отказа указана в комментариях. Вы можете связаться с нашей поддержкой для уточнений.<br><br>
						📝 <strong>Комментарий:</strong><br>
						<div 
                            style="font-style: italic;"
                            v-html="revisionComment"
                        ></div>
					</div>
				</template>

				<hr class="my-4">

				<p class="text-right font-italic">
					Хорошего дня! Будем на связи!
				</p>        
			</b-jumbotron>

			<!-- Расторжение договора по желанию агента -->
			<b-jumbotron style="max-width: 900px;" v-else-if="agentCompany.check_status === 'termination_by_agent'">
				<template #header>
					<h3 class="display-6">Заявка на расторжение договора подана 📋</h3>
				</template>

				<template #lead>
					<div style="margin-top: 35px; max-width: 750px;">
						Уважаемый агент! Мы получили вашу заявку на расторжение договора. <br><br>
						📅 <strong>Дата подачи заявки:</strong> {{ agentCompany.contract_terminated_at }}<br><br>
						⏳ Наши специалисты обработают вашу заявку в ближайшее время. В процессе расторжения договора:<br>
						• Ваша деятельность в качестве агента приостановлена<br>
						• Все активные заказы переданы нашим менеджерам<br>
						• Мы свяжемся с вами для урегулирования всех вопросов<br><br>
						Спасибо за сотрудничество! Мы ценим время, проведенное с нами.
					</div>
				</template>

				<hr class="my-4">

				<div class="text-right">
					<b-button
						variant="info"
						size="md"
						@click="fetchUserCompany"
					>Обновить статус 🔄</b-button>
				</div>
			</b-jumbotron>

			<!-- Расторжение договора по желанию компании -->
			<b-jumbotron style="max-width: 900px;" v-else-if="agentCompany.check_status === 'termination_by_company'">
				<template #header>
					<h3 class="display-6">Договор расторгнут компанией 🏢</h3>
				</template>

				<template #lead>
					<div style="margin-top: 35px; max-width: 750px;">
						Уважаемый агент! К сожалению, наша компания приняла решение о расторжении договора. <br><br>
						📅 <strong>Дата расторжения:</strong> {{ agentCompany.contract_terminated_at }}<br><br>
						🔒 Ваша деятельность в качестве агента прекращена. Все активные заказы переданы нашим менеджерам для завершения.<br><br>
						📞 Если у вас есть вопросы по расторжению договора, пожалуйста, свяжитесь с нашей поддержкой.<br><br>
						Спасибо за понимание и сотрудничество!
					</div>
				</template>

				<hr class="my-4">

				<p class="text-right font-italic">
					Желаем успехов в дальнейшей деятельности!
				</p>
			</b-jumbotron>

			<!-- Агент проверен -->
			<div v-else-if="agentCompany.check_status === 'approved_by_company'" class="agent-dashboard">
				<!-- HERO -->
				<b-card class="dashboard-hero-card mb-4" body-class="p-4 p-md-5">
					<div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
						<div class="mb-3 mb-md-0">
							<h2 class="mb-2 text-white">🎉 Поздравляем! Ваши данные проверены</h2>
							<p class="lead text-white-80 mb-0">Мы тщательно изучили все документы и информацию, которую вы предоставили</p>
						</div>
						<div class="text-nowrap hero-actions text-right">
						</div>
					</div>
				</b-card>

				<!-- Основной контент -->
				<b-row>
					<b-col cols="12" md="8">
						<b-card class="mb-4">
							<h4 class="card-title mb-3">Что мы проверили:</h4>
							<ul class="list-unstyled">
								<li class="mb-2">
									<i class="fas fa-check-circle text-success mr-2"></i>
									<strong>Контактная информация:</strong> Адрес, телефон, email
								</li>
								<li class="mb-2">
									<i class="fas fa-check-circle text-success mr-2"></i>
									<strong>Документы:</strong> ИНН, паспорт, банковские реквизиты и другие
								</li>
								<li class="mb-2">
									<i class="fas fa-check-circle text-success mr-2"></i>
									<strong>Бизнес-данные:</strong> Тип деятельности, регион работы
								</li>
								<li class="mb-2">
									<i class="fas fa-check-circle text-success mr-2"></i>
									<strong>Финансовые условия:</strong> Комиссионные ставки согласованы
								</li>
							</ul>
						</b-card>

						<b-card class="mb-4">
							<h4 class="card-title mb-3">🚀 Следующий шаг:</h4>
							<p class="mb-3">
								Для начала работы в системе вам необходимо принять условия договора оферты. 
								Это займет всего несколько минут и откроет доступ ко всем возможностям платформы.
							</p>
							<div class="text-center mt-4 mb-3">
								<b-button 
									variant="success" 
									size="lg"
									@click="showOfferModal = true"
                                    style="text-transform: uppercase; height: 50px;"
								>
									<i class="fas fa-file-contract mr-2"></i>
									Подписать оферту и начать работу
								</b-button>
							</div>
						</b-card>
					</b-col>

					<b-col cols="12" md="4">
						<b-card class="mb-4">
							<h5 class="card-title mb-3">💼 Ваши комиссии:</h5>
							<div class="commission-item mb-2">
								<div class="h5 mb-0 text-success font-weight-bold">{{ agentCompany.first_order_commission_rate || 30 }}%</div>
								<small class="text-muted">От маржинальной прибыли<br />с первой поставки</small>
							</div>
							<div class="commission-item mb-2">
								<div class="h5 mb-0 text-success font-weight-bold">{{ agentCompany.first_year_order_commission_rate || 25 }}%</div>
								<small class="text-muted">От маржинальной прибыли<br />в течение первого года</small>
							</div>
							<div class="commission-item mb-2">
								<div class="h5 mb-0 text-success font-weight-bold">{{ agentCompany.next_orders_commission_rate || 20 }}%</div>
								<small class="text-muted">От маржинальной прибыли до конца сотрудничества после первого года</small>
							</div>
						</b-card>

						<b-card>
							<h5 class="card-title mb-3">📞 Нужна помощь?</h5>
							<p class="mb-2">Если у вас есть вопросы по оферте или условиям сотрудничества, свяжитесь с нами в Telegram:</p>
							<div class="text-center mt-2">
								<a 
									href="https://t.me/TANAIS_OPERATION" 
									target="_blank" 
									class="btn btn-primary"
								>
									<i class="fab fa-telegram mr-2"></i>
									Написать в Telegram
								</a>
							</div>
						</b-card>
					</b-col>
				</b-row>
			</div>

			<!-- Одобрено -->
			<div v-else-if="agentCompany.check_status === 'approved'" class="agent-dashboard">
				<!-- HERO -->
				<b-card class="dashboard-hero-card mb-4" body-class="p-4 p-md-5">
					<div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
						<div class="mb-3 mb-md-0">
							<h2 class="mb-2 text-white d-none d-md-block">{{ greetingText }}</h2>
							<p class="lead text-white-80 mb-0">Ваш персональный рабочий кабинет агента</p>
						</div>
                        <div class="text-nowrap hero-actions text-right">
                            <b-button 
                                size="lg" 
                                class="h40px" 
                                variant="success" 
                                v-b-toggle.create-order-modal
                            >
                                <span class="font-weight-bold">+</span> Создать заявку
                            </b-button>

                            <!-- Период -->
                            <div class="mt-3 period-selector">
                                <label class="text-white-80 mb-0 mr-2 d-block">Период:</label>
                                <b-form-select
                                    v-model="selectedPeriod"
                                    :options="periodOptions"
                                    size="sm"
                                    class="period-select"
                                    @change="onPeriodChange"
                                ></b-form-select>

                                <b-button
                                    size="sm"
                                    variant="light"
                                    class="ml-2 refresh-btn"
                                    :disabled="isRefreshing"
                                    @click="softRefresh"
                                    title="Обновить"
                                    aria-label="Обновить"
                                >
                                    <span v-if="isRefreshing"><i class="fas fa-spinner fa-spin"></i></span>
                                    <span v-else aria-hidden="true">↻</span>
                                </b-button>
                            </div>                            
                        </div>
					</div>
				</b-card>

				<!-- KPIs -->
				<b-row>
                    <b-col cols="12" md="6" xl="3" class="mb-4">
						<b-card class="stat-card h-100">
							<span class="help-icon" v-b-tooltip.hover title="Начисленные агентские вознаграждения, доступные к выплате (начислено − выплачено − удержания).">
								<b-icon icon="question-circle"></b-icon>
							</span>
                            <div class="d-flex align-items-end justify-content-between">
                                <div class="stat-label">Доступно к выплате</div>
                            </div>
							<div class="d-flex align-items-end justify-content-between">
								<div>
                                    <div class="stat-value text-brand-green">
                                        <template v-if="dashboardLoading">
                                            <i class="fas fa-spinner fa-spin"></i>
                                        </template>
                                        <template v-else>
                                            {{ formatCurrency(displayed.commissionAvailable) }}
                                        </template>
                                    </div>
						        </div>
                                <b-button size="sm" variant="outline-secondary" class="mb13px" @click="openSettlements" title="Открыть" aria-label="Открыть">
                                    <b-icon icon="box-arrow-up-right"></b-icon>
                                </b-button>
							</div>
                            <div class="d-flex justify-content-start">
                                <b-button 
                                    size="sm" 
                                    variant="outline-success" 
                                    class="mb13px mb-0 mt-0" 
                                    @click="openWithdrawalModal" 
                                    title="Вывести средства" 
                                    aria-label="Вывести средства"
                                >
                                    Вывести деньги <i class="fas fa-money-bill-wave ml-1"></i>
                                </b-button>
                            </div>
						</b-card>
					</b-col>

					<b-col cols="12" md="6" xl="3" class="mb-4">
						<b-card class="stat-card h-100">
							<span class="help-icon" v-b-tooltip.hover :title="`Количество активных заявок (исключая закрытые и отменённые) / новых заявок за ${selectedPeriod !== 'month' ? getPeriodText() : '7 дней'}.`">
								<b-icon icon="question-circle"></b-icon>
							</span>
							<div class="d-flex align-items-end justify-content-between">
                                <div>
                                    <div class="stat-label">Заявки</div>
                                    <div class="stat-value">
                                        <template v-if="dashboardLoading">
                                            <i class="fas fa-spinner fa-spin"></i>
                                        </template>
                                        <template v-else>
                                            {{ displayed.ordersInProgress }} / {{ displayed.newLeads7d }}
                                        </template>
                                    </div>
                                </div>
                                <b-button size="sm" variant="outline-secondary" class="mb13px" @click="openOrders" title="Открыть" aria-label="Открыть">
                                    <b-icon icon="box-arrow-up-right"></b-icon>
                                </b-button>
							</div>
                            <div class="stat-label stat-mini-label">{{ selectedPeriod !== 'month' ? `Заявки в работе / за ${getPeriodText()}` : 'Заявки в работе / за 7 дн.' }}</div>
						</b-card>
					</b-col>

					<b-col cols="12" md="6" xl="3" class="mb-4">
						<b-card class="stat-card h-100">
							<span class="help-icon" v-b-tooltip.hover title="Поставки: в работе / ожидают оплаты / закрыты.">
								<b-icon icon="question-circle"></b-icon>
							</span>
							<div class="d-flex align-items-end justify-content-between">
                                <div>
                                    <div class="stat-label">Поставки</div>
                                    <div class="stat-value">
                                        <template v-if="dashboardLoading">
                                            <i class="fas fa-spinner fa-spin"></i>
                                        </template>
                                        <template v-else>
                                            {{ displayed.suppliesInProgress || 0 }} / {{ displayed.awaitingPayments }} / {{ displayed.suppliesClosed || 0 }} / {{ displayed.suppliesRejected || 0 }}
                                        </template>
                                    </div>
                                </div>
                                <b-button size="sm" variant="outline-secondary" class="mb13px" @click="openMultiTypeSupplies" title="Открыть" aria-label="Открыть">
                                    <b-icon icon="box-arrow-up-right"></b-icon>
                                </b-button>
							</div>
                            <div class="stat-label stat-mini-label">в работе / в оплате / закрыты / отказ</div>
						</b-card>
					</b-col>

					<!-- Блок "Создай свою команду" -->
					<b-col cols="12" md="6" xl="3" class="mb-4">
						<b-card class="team-card h-100" @click="goToMakeTeam">
							<div class="team-card-content">
								<div class="team-card-text">
                                    <div class="d-flex align-items-center">
                                        <div class="team-card-icon">
                                            <i class="fas fa-rocket"></i>
                                        </div>
                                        <h5 class="team-card-title">Создай свою команду</h5>
                                    </div>
                                    <div class="d-flex align-items-center justify-content-between">
                                        <p class="team-card-subtitle">Приглашай новых агентов и получай их процент</p>
                                        <div class="team-card-arrow">
                                            <i class="fas fa-arrow-right"></i>
                                        </div>
                                    </div>
								</div>
							</div>
						</b-card>
					</b-col>

                    <b-col cols="12" md="6" xl="3" class="mb-4">
						<b-card class="stat-card h-100">
							<span class="help-icon" v-b-tooltip.hover :title="`Общая выручка от всех завершенных поставок за ${selectedPeriod !== 'month' ? getPeriodText() : 'месяц'}. В расчет берутся заказы со статусами Завершен, Ожидает оплаты, Закрыт.`">
								<b-icon icon="question-circle"></b-icon>
							</span>
                            <div class="d-flex align-items-end justify-content-between">
                                <div>
                                    <div class="stat-label">{{ selectedPeriod !== 'month' ? `Выручка за ${getPeriodText()}` : 'Выручка' }}</div>
                                    <div class="stat-value">
                                        <template v-if="dashboardLoading">
                                            <i class="fas fa-spinner fa-spin"></i>
                                        </template>
                                        <template v-else>
                                            {{ formatCurrency(displayed.revenue) }}
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </b-card>
                    </b-col>

                    <b-col cols="12" md="6" xl="3" class="mb-4">
						<b-card class="stat-card h-100">
							<span class="help-icon" v-b-tooltip.hover :title="`Суммарная маржинальная прибыль (выручка − себестоимость) по вашим заявкам за ${selectedPeriod !== 'month' ? getPeriodText() : 'месяц'}. В расчет берутся заказы со статусами Завершен, Ожидает оплаты, Закрыт.`">
								<b-icon icon="question-circle"></b-icon>
							</span>
                            <div class="d-flex align-items-end justify-content-between">
                                <div>
                                    <div class="stat-label">{{ selectedPeriod !== 'month' ? `Маржинальность за ${getPeriodText()}` : 'Маржинальность' }}</div>
                                    <div class="stat-value">
                                        <template v-if="dashboardLoading">
                                            <i class="fas fa-spinner fa-spin"></i>
                                        </template>
                                        <template v-else>
                                            {{ formatCurrency(displayed.marginRate) }}
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </b-card>
                    </b-col>

                    <b-col cols="12" md="6" xl="3" class="mb-4">
						<b-card class="stat-card h-100">
							<span class="help-icon" v-b-tooltip.hover :title="`Средняя сумма заказа (выручка / количество заказов) за ${selectedPeriod !== 'month' ? getPeriodText() : 'месяц'}.`">
								<b-icon icon="question-circle"></b-icon>
							</span>
                            <div class="d-flex align-items-end justify-content-between">
                                <div>
                                    <div class="stat-label">{{ selectedPeriod !== 'month' ? `Средний чек за ${getPeriodText()}` : 'Средний чек' }}</div>
                                    <div class="stat-value">
                                        <template v-if="dashboardLoading">
                                            <i class="fas fa-spinner fa-spin"></i>
                                        </template>
                                        <template v-else>
                                            {{ formatCurrency(displayed.avgCheck) }}
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </b-card>
                    </b-col>

					<b-col cols="12" md="6" xl="3" class="mb-4">
						<b-card class="stat-card h-100">
							<span class="help-icon" v-b-tooltip.hover :title="`Доля заявок, завершившихся успешной сделкой за ${selectedPeriod !== 'month' ? getPeriodText() : 'месяц'}, в процентах.`">
								<b-icon icon="question-circle"></b-icon>
							</span>
                            <div class="stat-label">{{ selectedPeriod !== 'month' ? `Конверсия за ${getPeriodText()}` : 'Конверсия' }}</div>
							<div class="stat-value">
                                <template v-if="dashboardLoading">
                                    <i class="fas fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    {{ displayed.conversionRate }}%
                                </template>
                            </div>
						</b-card>
					</b-col>
				</b-row>

				<!-- Рейтинг агентов и новости -->
				<b-row class="mb-4">
					<!-- Рейтинг агентов -->
					<b-col cols="12" lg="6" class="mb-4">
						<b-card class="stat-card h-100">
							<AgentRating />
						</b-card>
					</b-col>

					<!-- Новости -->
					<b-col cols="12" lg="6" class="mb-4">
						<b-card class="stat-card h-100">
							<AgentNewsWidget />
						</b-card>
					</b-col>
				</b-row>

				<!-- Lists -->
				<b-row>
					<b-col cols="12" lg="6" class="mb-4">
						<b-card class="stat-card h-100">
							<div class="d-flex align-items-center justify-content-between mb-3">
								<h5 class="mb-0">📋 Заявки в работе</h5>
                                <div class="d-flex">
                                    <b-button size="sm" variant="outline-secondary" class="mb13px mr-2" @click="openOrders" title="Заявки в работе" aria-label="Заявки в работе">
                                        <b-icon icon="box-arrow-up-right"></b-icon>
                                    </b-button>
                                    <b-button size="sm" variant="outline-info" class="mb13px" @click="openNewLeads" :title="`Новые заявки за ${selectedPeriod !== 'month' ? getPeriodText() : '7 дней'}`" aria-label="Новые заявки">
                                        <b-icon icon="clock"></b-icon>
                                    </b-button>
                                </div>
							</div>
                            <div v-if="dashboardLoading" class="text-center py-3"><i class="fas fa-spinner fa-spin"></i></div>
                            <div v-else-if="ordersInProgressList.length === 0" class="text-muted">Нет активных заявок 💤</div>
                            <div v-else>
								<ul class="list-unstyled mb-0">
									<li v-for="order in ordersInProgressList" :key="order.id" class="media order-item py-2">
										<div class="media-body d-flex align-items-center justify-content-between w-100">
											<div>
												<div class="font-weight-600">{{ order.client }}</div>
												<small class="text-muted">Статус: {{ order.stage }}</small>
											</div>
                                            <div class="text-right">
                                                <div class="sidebar-link text-primary font-weight-bold" @click="openOrderSidebar(order.id, '', '')">#{{ order.id }}</div>
                                                <small class="text-muted">Обновлено: {{ order.updated }}</small>
                                            </div>
										</div>
									</li>
								</ul>
							</div>
						</b-card>
					</b-col>

					<b-col cols="12" lg="6" class="mb-4">
						<b-card class="stat-card h-100">
							<div class="d-flex align-items-center justify-content-between mb-3">
								<h5 class="mb-0">📦 Поставки: в работе / в оплате / закрыты / отказ</h5>
                                <div class="d-flex">
                                    <b-button size="sm" variant="outline-secondary" class="mb13px" @click="openMultiTypeSupplies" title="Открыть" aria-label="Открыть">
                                        <b-icon icon="box-arrow-up-right"></b-icon>
                                    </b-button>
                                </div>
							</div>
                            <div v-if="dashboardLoading" class="text-center py-3"><i class="fas fa-spinner fa-spin"></i></div>
                            <div v-else-if="allSuppliesList.length === 0" class="text-muted">Нет поставок 👌</div>
                            <div v-else>
								<ul class="list-unstyled mb-0">
									<li v-for="item in allSuppliesList" :key="item.id" class="media payment-item py-2">
										<div class="media-body d-flex align-items-center justify-content-between w-100">
											<div>
								                <div class="font-weight-600">{{ item.supplier }}</div>
                                                <small class="text-muted">{{ item.status }}</small>
											</div>
                                            <div class="text-right">
                                                <small class="text-muted">{{ item.updated }}</small>
                                                <br>
                                                <small class="sidebar-link text-primary font-weight-bold" @click="openOrderSidebar(item.id, '', '')">#{{ item.id }}</small>
                                            </div>
										</div>
									</li>
								</ul>
							</div>
						</b-card>
					</b-col>
				</b-row>
			</div>

            <b-sidebar
                v-model="isSidebarOpen"
                id="sidebar-right-create-or-edit-agent-company"
                right
                backdrop
                shadow
                width="65em"
                backdrop-variant="dark"
                ref="editItem-agentCompany"
                no-close-on-backdrop
            >
                <template #title>
                    <div>
                        <strong id="sidebar-right___title__">Профиль агента</strong>
                    </div>
                </template>
                <div class="d-block">
                    <AgentCompanyProfile 
                        :agent_company="agentCompany" 
                        @closed="isSidebarOpen=false"
                        @updated="profileUpdated"
                    />
                </div>
            </b-sidebar>

            <OrderList
                v-if="orderListSidebar.open"
                :openOrderList="orderListSidebar.open"
                :orderList="orderListSidebar.ids"
                :label="orderListSidebar.label"
                :ownFields="orderListSidebar.fields"
                @closeOrderList="orderListSidebar.open = false"
            />

			<!-- Modal for creating orders (toggled via v-b-toggle.create-order-modal) -->
			<OrderCreateModal />

			<!-- Modal for withdrawal -->
			<AgentWithdrawalModal
				:open.sync="withdrawalModalOpen"
				:available-amount="displayed.commissionAvailable"
				:total-earned="totalEarned"
				:total-withdrawn="totalWithdrawn"
				:has-completed-deals="hasCompletedDeals"
				:agent-company-id="agentCompany ? agentCompany.id : null"
				:agent-company-type="agentCompany ? agentCompany.type : null"
				@withdrawal-requested="onWithdrawalRequested"
			/>

			<!-- Modal for offer agreement -->
			<AgentsOfertaModal
				:visible.sync="showOfferModal"
				:agent-company-id="agentCompany ? agentCompany.id : null"
				:commission-rates="{
					first_order: agentCompany ? agentCompany.first_order_commission_rate || 30 : 30,
					first_year: agentCompany ? agentCompany.first_year_order_commission_rate || 25 : 25,
					next_orders: agentCompany ? agentCompany.next_orders_commission_rate || 20 : 20
				}"
				@offer-accepted="handleOfferAccepted"
			/>
		</template>
	</div>
</template>

<script>
    import SkeletonBuilder from "@/components/ui/Loaders/SkeletonBuilder";
    import AgentCompanyProfile from "@/components/agentCompanies/AgentCompanyProfile";
    import OrderCreateModal from "@/components/orders/OrderCreateModal";
    import OrderList from "@/components/orders/OrderList.vue";
    import AgentWithdrawalModal from "@/components/agentWithdrawalRequests/AgentWithdrawalModal.vue";
    import AgentRating from "@/components/dashboards/AgentRating.vue";
    import AgentNewsWidget from "@/components/news/AgentNewsWidget.vue";
    import AgentsOfertaModal from "@/components/agent/AgentsOfertaModal.vue";
    import { openOrderSidebar } from "@/mixins/openOrderSidebar";
    import { API_AGENT_DASHBOARD_SUMMARY, API_AGENT_SETTLEMENTS } from "@/constants/api";

    export default {
        components: {
            SkeletonBuilder,
            AgentCompanyProfile,
            OrderCreateModal,
            OrderList,
            AgentWithdrawalModal,
            AgentRating,
            AgentNewsWidget,
            AgentsOfertaModal
        },    
        mixins: [openOrderSidebar],
        data() {
            return {
                loading: true,
                isSidebarOpen: false,
                revisionComment: '',
                isRefreshing: false,
                dashboardLoading: true,
                dashboard: {
                    commissionAvailable: 0,
                    ordersInProgress: 0,
                    awaitingPayments: 0,
                    newLeads7d: 0,
                    suppliesInProgress: 0,
                    suppliesClosed: 0,
                    suppliesRejected: 0,
                    revenue: 0,
                    avgCheck: 0,
                    conversionRate: 0,
                    marginRate: 0
                },
                displayed: {
                    commissionAvailable: 0,
                    ordersInProgress: 0,
                    awaitingPayments: 0,
                    newLeads7d: 0,
                    suppliesInProgress: 0,
                    suppliesClosed: 0,
                    suppliesRejected: 0,
                    revenue: 0,
                    avgCheck: 0,
                    conversionRate: 0,
                    marginRate: 0
                },
                rafIds: {},
                awaitingPaymentsList: [],
                ordersInProgressList: [],
                allSuppliesList: [],
                orderListSidebar: {
                    open: false,
                    ids: [],
                    label: 'Список поставок',
                    fields: [],
                },


                withdrawalModalOpen: false,
                totalEarned: 0,
                totalWithdrawn: 0,
                hasCompletedDeals: false,
                selectedPeriod: 'month',
                periodOptions: [
                    { value: 'month', text: 'Месяц' },
                    { value: 'quarter', text: 'Квартал' },
                    { value: 'halfYear', text: 'Полгода' },
                    { value: 'year', text: 'Год' }
                ],
                showOfferModal: false
            }
        },
        computed: {
            agentCompany() {
                return this.$store.getters['auth/getAgentCompany'];
            },
            greetingText() {
                const hours = new Date().getHours();
                const dayPart = hours < 12 ? 'Доброе утро' : (hours < 18 ? 'Добрый день' : 'Добрый вечер');
                let name = 'коллега';
                try {
                    if (this.agentCompany) {
                        name = this.agentCompany.short_name || this.agentCompany.name || this.agentCompany.legal_name || name;
                    }
                } catch (e) { /* noop */ }
                return `${dayPart}, ${name}!`;
            }
        },
        mounted() {
            this.fetchUserCompany();
            
            // Проверяем, нужно ли показать уведомление о блокировке роутов
            this.checkBlockedRouteNotification();
        },
        methods: {
            handleOfferAccepted() {
                // Обновляем данные агентской компании после принятия оферты
                this.$store.dispatch('auth/refreshAgentCompany').then(() => {
                    // Показываем уведомление об успешном обновлении
                    this.$bvToast.toast('Статус обновлен! Теперь вы можете работать с системой.', {
                        variant: 'success',
                        title: 'Статус обновлен'
                    });
                }).catch(err => {
                    console.error('Ошибка обновления статуса:', err);
                });
            },
            
            checkBlockedRouteNotification() {
                // Проверяем, нужно ли показать уведомление о блокировке роутов
                const showMessage = sessionStorage.getItem('showBlockedRouteMessage');
                const blockedRoute = sessionStorage.getItem('blockedRoute');
                
                if (showMessage === 'true' && blockedRoute) {
                    // Показываем уведомление
                    this.$bvToast.toast(`Доступ к разделу "${blockedRoute}" заблокирован в связи с расторжением договора`, {
                        title: 'Доступ ограничен',
                        variant: 'warning',
                        solid: true,
                        autoHideDelay: 8000
                    });
                    
                    // Очищаем данные из sessionStorage
                    sessionStorage.removeItem('showBlockedRouteMessage');
                    sessionStorage.removeItem('blockedRoute');
                }
            },
            
            fetchUserCompany() {
                this.loading = true; 
                this.$store.dispatch('auth/refreshAgentCompany').then(company => {
                    this.loading = false;
                    this.revisionComment = this.getLatestComment(company);
                    this.loadDashboard();
                }).catch(err => {
                    console.error('Ошибка обновления:', err);
                    this.loading = false; 
                });
            },
            async softRefresh() {
                if (this.isRefreshing) return;
                this.isRefreshing = true;
                try {
                    const company = await this.$store.dispatch('auth/refreshAgentCompany');
                    this.revisionComment = this.getLatestComment(company);
                    await this.loadDashboard();
                } catch (e) {
                    console.error('Ошибка обновления:', e);
                } finally {
                    setTimeout(() => { this.isRefreshing = false; }, 400); // лёгкая задержка для UX
                }
            },
            async loadDashboard() {
                try {
                    this.dashboardLoading = true;
                    const params = { period: this.selectedPeriod };
                    const { data } = await api.call('get', API_AGENT_DASHBOARD_SUMMARY, { params });
                    this.dashboard.commissionAvailable = data?.commissionAvailable ?? 0;
                    this.dashboard.ordersInProgress = data?.ordersInProgress ?? 0;
                    this.dashboard.awaitingPayments = data?.awaitingPayments ?? 0;
                    this.dashboard.newLeads7d = data?.newLeads7d ?? 0;
                    this.dashboard.suppliesInProgress = data?.suppliesInProgress ?? 0;
                    this.dashboard.suppliesClosed = data?.suppliesClosed ?? 0;
                    this.dashboard.suppliesRejected = data?.suppliesRejected ?? 0;
                    this.dashboard.revenue = data?.revenue ?? 0;
                    this.dashboard.avgCheck = data?.avgCheck ?? 0;
                    this.dashboard.conversionRate = data?.conversionRate ?? 0;
                    this.dashboard.marginRate = data?.marginRate ?? 0; // пока заглушка с бэка

                    this.awaitingPaymentsList = data?.lists?.awaitingPayments ?? [];
                    this.ordersInProgressList = data?.lists?.ordersInProgress ?? [];
                    this.allSuppliesList = data?.lists?.allSupplies ?? [];

                    // store ids for quick open
                    this.orderIdsInProgress = data?.orderIdsInProgress ?? [];
                    this.orderIdsAwaitingPayments = data?.orderIdsAwaitingPayments ?? [];
                    this.orderIdsNewLeads7d = data?.orderIdsNewLeads7d ?? [];
                    this.supplyIdsInProgress = data?.supplyIdsInProgress ?? [];
                    this.supplyIdsClosed = data?.supplyIdsClosed ?? [];
                    this.supplyIdsRejected = data?.supplyIdsRejected ?? [];
                    
                    // Данные для вывода средств
                    this.totalEarned = data?.totalEarned ?? 0;
                    this.totalWithdrawn = data?.totalWithdrawn ?? 0;
                    
                    // Проверяем завершенные сделки
                    await this.checkCompletedDeals();
                    
                    this.animateDashboardValues();
                } catch (e) {
                    console.error('Не удалось загрузить данные дашборда агента:', e);
                    // оставляем нули/пустые списки
                } finally {
                    // небольшая задержка, чтобы не мигал спиннер
                    setTimeout(() => { this.dashboardLoading = false; }, 150);
                }
            },
            getLatestComment(sourceCompany = null) {
                try {
                    const company = sourceCompany || this.agentCompany || {};
                    const latest = company && company.latest_status_with_comments;
                    const comments = latest && latest.user_comments;
                    if (comments && Array.isArray(comments) && comments.length > 0) {
                        let comment = `<div>${comments[0].comment}</div>
                                       <div class=\"text-right\"><small style=\"font-size: 12px;\">Дата сообщения: ${comments[0].created_at}</small></div>`;
                        return comment;
                    }
                } catch (e) {
                    console.error("Ошибка при парсинге this.agentCompany.latest_status_with_comments в DashboardAgent:", e);
                }

                return 'Не указан'; 
            },
            profileUpdated() {
                this.isSidebarOpen = false;
                this.fetchUserCompany();
            },
            // демо-данные удалены
            formatCurrency(value) {
                try {
                    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(Number(value || 0));
                } catch (e) {
                    return `${value} ₽`;
                }
            },
            formatPercent(value) {
                const v = Number(value || 0);
                return `${Math.round(v)}%`;
            },
            animateNumber(field, toValue, duration = 1200) {
                try {
                    const startValue = Number(this.displayed[field] || 0);
                    const endValue = Number(toValue || 0);
                    if (startValue === endValue || duration <= 0) {
                        this.displayed[field] = endValue;
                        return;
                    }
                    const startTime = performance.now();
                    const animate = (now) => {
                        const elapsed = now - startTime;
                        const progress = Math.min(elapsed / duration, 1);
                        const easeOut = 1 - Math.pow(1 - progress, 3);
                        this.displayed[field] = Math.round(startValue + (endValue - startValue) * easeOut);
                        if (progress < 1) {
                            this.rafIds[field] = requestAnimationFrame(animate);
                        }
                    };
                    if (this.rafIds[field]) {
                        cancelAnimationFrame(this.rafIds[field]);
                    }
                    this.rafIds[field] = requestAnimationFrame(animate);
                } catch (e) {
                    this.displayed[field] = Number(toValue || 0);
                }
            },
            animateDashboardValues() {
                this.animateNumber('commissionAvailable', this.dashboard.commissionAvailable);
                this.animateNumber('ordersInProgress', this.dashboard.ordersInProgress);
                this.animateNumber('awaitingPayments', this.dashboard.awaitingPayments);
                this.animateNumber('newLeads7d', this.dashboard.newLeads7d);
                this.animateNumber('suppliesInProgress', this.dashboard.suppliesInProgress);
                this.animateNumber('suppliesClosed', this.dashboard.suppliesClosed);
                this.animateNumber('suppliesRejected', this.dashboard.suppliesRejected);
                this.animateNumber('revenue', this.dashboard.revenue);
                this.animateNumber('avgCheck', this.dashboard.avgCheck);
                this.animateNumber('conversionRate', this.dashboard.conversionRate);
                this.animateNumber('marginRate', this.dashboard.marginRate);
            },
            cancelAnimations() {
                Object.keys(this.rafIds).forEach((k) => {
                    try { cancelAnimationFrame(this.rafIds[k]); } catch (e) {}
                });
                this.rafIds = {};
            },
            openMultiTypeSupplies() {
                // open order list by all supply IDs (in progress + awaiting + closed + rejected)
                this.orderListSidebar.label = 'Все поставки (в работе / ожидают / закрыты / отказ)';
                const allSupplyIds = [
                    ...(this.supplyIdsInProgress || []),
                    ...(this.orderIdsAwaitingPayments || []),
                    ...(this.supplyIdsClosed || []),
                    ...(this.supplyIdsRejected || [])
                ];
                this.orderListSidebar.ids = allSupplyIds.map(id => ({ order_id: id }));
                this.orderListSidebar.open = true;
            },
            openSettlements() {
                if (this.$router) {
                    this.$router.push({ path: '/agent-settlements' }).catch(() => {});
                } else {
                    console.log('Открыть взаиморасчёты агента');
                }
            },
            openOrders() {
                // open order list by in-progress IDs
                this.orderListSidebar.label = 'Заявки в работе';
                this.orderListSidebar.ids = Array.isArray(this.orderIdsInProgress) ? this.orderIdsInProgress.map(id => ({ order_id: id })) : [];
                this.orderListSidebar.open = true;
            },
            openNewLeads() {
                this.orderListSidebar.label = `Новые заявки за ${this.selectedPeriod !== 'month' ? this.getPeriodText() : '7 дней'}`;
                this.orderListSidebar.ids = Array.isArray(this.orderIdsNewLeads7d) ? this.orderIdsNewLeads7d.map(id => ({ order_id: id })) : [];
                this.orderListSidebar.open = true;
            },

            openCreateOrder() {
                if (this.$router) {
                    this.$router.push({ path: '/orders/create' }).catch(() => {});
                } else {
                    console.log('Создать заявку');
                }
            },
            goToMakeTeam() {
                if (this.$router) {
                    this.$router.push({ path: '/make-your-team' }).catch(() => {});
                } else {
                    console.log('Переход к созданию команды');
                }
            },


            async checkCompletedDeals() {
                try {
                    if (!this.agentCompany || !this.agentCompany.id) {
                        this.hasCompletedDeals = false;
                        return;
                    }

                    // Загружаем settlements для проверки завершенных сделок
                    // Загружаем только approved и completed статусы
                    const [approvedResponse, completedResponse] = await Promise.all([
                        api.call('get', API_AGENT_SETTLEMENTS, {
                            params: {
                                agent_company_id: this.agentCompany.id,
                                status: 'approved',
                                per_page: 100
                            }
                        }),
                        api.call('get', API_AGENT_SETTLEMENTS, {
                            params: {
                                agent_company_id: this.agentCompany.id,
                                status: 'completed',
                                per_page: 100
                            }
                        })
                    ]);

                    const approvedSettlements = approvedResponse.data?.data || [];
                    const completedSettlements = completedResponse.data?.data || [];
                    const allSettlements = [...approvedSettlements, ...completedSettlements];
                    
                    // Проверяем, есть ли хотя бы одна запись со статусом approved или completed и не пустым order_id
                    this.hasCompletedDeals = allSettlements.some(settlement => 
                        (settlement.status === 'approved' || settlement.status === 'completed') &&
                        settlement.order_id !== null &&
                        settlement.order_id !== undefined &&
                        settlement.order_id !== ''
                    );
                } catch (error) {
                    console.error('Ошибка при проверке завершенных сделок:', error);
                    this.hasCompletedDeals = false;
                }
            },
            openWithdrawalModal() {
                this.withdrawalModalOpen = true;
            },
            onWithdrawalRequested(withdrawalData) {
                console.log('Заявка на вывод средств:', withdrawalData);
                // Здесь можно добавить логику обновления данных после успешной заявки
                this.softRefresh();
            },
            onPeriodChange() {
                console.log('Период изменен на:', this.selectedPeriod);
                // Перезагружаем данные с новым периодом
                this.loadDashboard();
            },
            getPeriodText() {
                const periodMap = {
                    'month': 'месяц',
                    'quarter': 'квартал',
                    'halfYear': 'полгода',
                    'year': 'год'
                };
                return periodMap[this.selectedPeriod] || 'месяц';
            }
        }
    }
</script>

<style scoped>
.agent-dashboard {
    max-width: 100%;
}

.dashboard-hero-card {
    background: linear-gradient(135deg, #0d6348 0%, #e37926 100%);
    border: none;
    color: #fff;
    border-radius: 14px;
}

.text-white-80 { color: rgba(255, 255, 255, 0.85); }

.text-brand-green { color: #0d6348; }
.bg-brand-green { background-color: #0d6348; }
.text-brand-orange { color: #e37926; }
.bg-brand-orange { background-color: #e37926; }

.stat-card {
    border: none;
    border-radius: 14px;
    box-shadow: 0 6px 20px rgba(0,0,0,0.05);
}

.stat-label {
    font-size: 0.85rem;
    line-height: 0.85rem;
    color: #6c757d;
    margin-bottom: 6px;
}

.stat-value {
    font-size: 2rem;
    font-weight: 700;
}

.stat-mini-label {
    font-size: 0.75rem;
    color: #6c757d;
    margin-bottom: 4px;
}

.stat-mini-value {
    font-size: 1.25rem;
    font-weight: 700;
}

.payment-item, .order-item {
    border-top: 1px solid #f2f2f2;
}
.payment-item:first-child, .order-item:first-child { border-top: 0; }

.font-weight-600 { font-weight: 600; }

.hero-actions .refresh-btn {
    font-size: 16px !important;
    width: 50px !important;
    height: 30px !important;
    line-height: 16px !important;
    padding: 0;
    opacity: 0.85;
    border-color: rgba(255,255,255,0.35) !important;
    color: rgba(255,255,255,0.9) !important;
	background: transparent !important;
}
.hero-actions .refresh-btn:hover {
    opacity: 1;
	border-color: #0d6247 !important;
}
.h40px { height: 40px; }
.mb13px { margin-bottom: 13px; }
.help-icon {
    position: absolute;
    right: 30px;
    cursor: pointer;
}

.agent-name {
    font-size: 0.95rem;
    color: #495057;
}

.period-selector {
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.period-select {
    min-width: 100px;
    max-width: 120px;
    background-color: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 0.2rem;
}

.commission-item {
    padding: 8px 0;
    border-bottom: 1px solid #f8f9fa;
}

.commission-item:last-child {
    border-bottom: none;
}

.contact-info {
    font-size: 0.9rem;
}

.contact-info i {
    width: 16px;
}

.period-select:focus {
    background-color: white;
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.25);
}

/* Стили для блока "Создай свою команду" */
.team-card {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border: 3px solid transparent;
    background-clip: padding-box;
    border-radius: 16px;
    box-shadow: 0 8px 25px rgba(13, 99, 72, 0.15);
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden;
    position: relative;
    background-image: 
        linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%),
        linear-gradient(135deg, #0d6348 0%, #e37926 100%);
    background-origin: border-box;
    background-clip: padding-box, border-box;
}

.team-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(13, 99, 72, 0.05), transparent);
    transition: left 0.6s;
}

.team-card:hover::before {
    left: 100%;
}

.team-card:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 12px 35px rgba(13, 99, 72, 0.25);
    border-color: #0d6348;
}

.team-card-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    padding: 0px;
    color: #2c3e50;
}

.team-card-icon {
    font-size: 1.5rem;
    margin-right: 15px;
    animation: rocketFloat 3s ease-in-out infinite;
    background: linear-gradient(135deg, #0d6348 0%, #e37926 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

@keyframes rocketFloat {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-8px) rotate(5deg); }
}

.team-card-text {
    flex: 1;
}

.team-card-title {
    font-size: 1rem;
    font-weight: 700;
    margin: 0 0 15px 0;
    background: linear-gradient(135deg, #0d6348 0%, #e37926 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.team-card-subtitle {
    font-size: 0.9rem;
    margin: 0 10px 0 0;
    color: #6c757d;
    line-height: 1.3;
    max-width: 180px;
}

.team-card-arrow {
    font-size: 1.5rem;
    opacity: 0.8;
    transition: all 0.3s ease;
    margin-right: 10px;
    background: linear-gradient(135deg, #0d6348 0%, #e37926 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.team-card:hover .team-card-arrow {
    transform: translateX(5px);
    opacity: 1;
}

/* Адаптивность для мобильных устройств */
@media (max-width: 768px) {
    .team-card-content {
        flex-direction: column;
        text-align: center;
        padding: 0px;
    }
    
    .team-card-icon {
        margin-right: 0;
        margin-bottom: 10px;
        font-size: 1.5rem;
    }
    
    .team-card-arrow {
        font-size: 1.2rem;
        margin-right: 0;
    }
    
    .team-card-title {
        font-size: 1rem;
    }
    
    .team-card-subtitle {
        font-size: 0.8rem;
        color: #6c757d;
    }
}
</style>